'use strict';

import { fetch as fetchPolyfill } from 'whatwg-fetch';
import { loadWidget } from '../utils/load-widget';
import { debounce, get, isString, sortBy, uniq } from 'lodash';
import {initialiseElement} from '../../modules/scroller';
import { DateTime, Settings } from 'luxon';
import image from '../../utils/image-manipulator'

const TIMEZONE = 'America/Chicago';

Settings.defaultZoneName = TIMEZONE;

const now = DateTime.now();

loadWidget('search-results', {
    props: [
        'searchPageUrl',
        'searchEngineKey',
        'searchInputSelector',
    ],
    data: () => ({
        ViewModel: {
            "MoreConcertsResultLink": '',
            "Loading": true,
            "Results": [
            ],
            "TotalResults": 0,
            "CurrentPage": null,
            "TotalPages": null,
            "Pagination": [],
            "Tabs": [],
            "Query": null,
            "UpcomingSlider": [],
            "UpcomingTotal": 0,
            inputField: null,
            SearchSuggestions: [],
        },
        options: {
            pageParam: 'page',
            resultsPerPage: 15,
            queryParam: 'q',
            activeTabParam: 'show',
        },
    }),
    methods: {
        init: function() {
            this.ViewModel.Query = this.getQuery();
            this.updateSearch();
        },
        updateSearch() {
            if (!this.ViewModel.Query) {
                return (this.ViewModel.Loading = false);
            }
            const promises = [
                this.getResults(this.ViewModel.Query),
            ];
            if (this.getActiveTab() === 'all') {
                // Only do explicit upcoming events search on "All" tab
                promises.push(this.getUpcomingEventsResults(this.ViewModel.Query));
            }
            Promise.all(promises)
                .then(data => {
                    this.updateTabs();
                    this.updateInput();

                    if (data[0].record_count || data[1].record_count) {
                        return this.showResults(data[0], data[1]);
                    }

                    this.ViewModel.Loading = false;
                });
        },
        getSuggestionsListener: function () {
            return debounce(() => {
                this.getSuggestions(this.ViewModel.inputField.value)
                    .then(this.processSuggestions)
                    .then(this.updateSuggestionList);
            }, 250);
        },
        processSuggestions: function (results) {
            let suggestions = [];

            if ('spelling_suggestion' in results.info.page && results.record_count === 0) {
                suggestions.push(results.info.page.spelling_suggestion.text);
            }

            suggestions = results.records.page.reduce((carry, record) => {
                if ('sections' in record.highlight) {
                    const sectionTexts = this.tokenise(record.highlight.sections);

                    sectionTexts.forEach(t => {
                        if (t.split(' ').length <= 6) {
                            carry.push(t);
                        }
                    });
                }

                if ('internal_title' in record.highlight) {
                    const internalTitleTexts = this.tokenise(record.highlight.internal_title);

                    internalTitleTexts.forEach(t => {
                        if (t.split(' ').length <= 6) {
                            carry.push(t);
                        }
                    });
                }

                const titleTexts = this.tokenisePlain(record.title, this.ViewModel.inputField);

                titleTexts.forEach(t => {
                    if (t.split(' ').length <= 6) {
                        carry.push(t);
                    }
                });

                return carry;
            }, suggestions);

            suggestions = uniq(suggestions);

            // Sort by length
            suggestions = sortBy(suggestions, suggestion => {
                return suggestion.length;
            });

            // Only show first 10
            suggestions = suggestions.slice(0, 10);

            return suggestions;
        },
        tokenise(phrase) {
            // lower the case
            let phrases = phrase.toLowerCase();

            // split by em
            phrases = phrases.split('<em>');

            // remove first one as it's junk or empty
            phrases.shift();

            return phrases.map(p => {
                // remove the closing tag
                p = p.replace('</em>', '');

                // amperstand
                p = p.replace('&amp;', '');

                // remove non alphanumeric and space
                p = p.replace(/[^A-Za-z0-9 -]/g, '');

                // excessive spaces
                p = p.replace(/\s{2,}/, ' ');

                return p.trim();
            });
        },
        tokenisePlain(phrase, q) {
            // lower the case
            let phrases = phrase.toLowerCase();

            // split by phrase
            phrases = phrases.split(q);

            // remove the first one as it's junk or empty
            phrases.shift();

            return phrases.map(p => {
                // append the query again
                p = q + p;

                // amperstand
                p = p.replace('&amp;', '');

                // remove non alphanumeric and space
                p = p.replace(/[^A-Za-z0-9 -]/g, '');

                // excessive spaces
                p = p.replace(/\s{2,}/, ' ');

                return p.trim();
            });
        },
        updateSuggestionList(suggestions) {
            this.ViewModel.SearchSuggestions = suggestions.map(suggestion => {
                const url = new URL(this.searchPageUrl);  // '/search/?q=' + suggestion,
                url.searchParams.append(this.options.queryParam, suggestion);

                return {
                    plain: suggestion,
                    highlighted: suggestion.replace(new RegExp(this.ViewModel.inputField.value, 'ig'), '<strong>$&</strong>'),
                    url: url.toString(),
                    click: () => {
                        this.ViewModel.Loading = true;
                        this.ViewModel.SearchSuggestions = [];
                        this.ViewModel.Query = suggestion;
                        this.updateSearch();
                    }
                }
            });
        },
        getQueryParams: function () {
            return new URLSearchParams(window.location.search);
        },
        getQuery: function () {
            return this.getQueryParams().get(this.options.queryParam);
        },
        hasPage: function () {
            return this.getQueryParams().has(this.options.pageParam);
        },
        getPage: function (defaultValue) {
            return parseInt(this.getQueryParams().get(this.options.pageParam), 10) || defaultValue || 1;
        },
        hasActiveTab: function () {
            return this.getQueryParams().has(this.options.activeTabParam);
        },
        getActiveTab: function (defaultValue) {
            return this.getQueryParams().get(this.options.activeTabParam) || defaultValue || 'all';
        },
        makeUrlWithPage: function (query, page) {
            return this.makeUrlWithBoth(query, page, null);
        },
        makeUrlWithTab: function (query, tab) {
            return this.makeUrlWithBoth(query, null, tab);
        },
        makeUrlWithBoth: function (query, page, tab) {
            if (!query) {
                return '';
            }

            query = query
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');

            page = parseInt(page, 10);

            let params = new URLSearchParams();

            params.append(this.options.queryParam, query);

            if (page) {
                params.append(this.options.pageParam, page);
            }

            if (isString(tab) && tab.length) {
                params.append(this.options.activeTabParam, tab);
            } else if (this.hasActiveTab()) {
                params.append(this.options.activeTabParam, this.getActiveTab());
            }

            const separator = this.searchPageUrl.indexOf('?') > -1 ? '&' : '?';
            return this.searchPageUrl + separator + params.toString();
        },
        makeSwiftypeUrl: function (query) {
            let params = {
                q: query,
                per_page: this.options.resultsPerPage,
                spelling: 'always',
                engine_key: this.searchEngineKey,
            }

            if (this.hasPage()) {
                params['page'] = this.getPage();
            }

            params = new URLSearchParams(params);

            params.append('highlight_fields[page][body][size]', 275 + 25); // we'll trim to 275, but give it a buffer
            params.append('highlight_fields[page][body][fallback]', true);

            if (this.hasActiveTab()) {
                switch (this.getActiveTab()) {
                    case 'event':
                        params.append('filters[page][type][]', 'event');
                        break;
                    case 'stories':
                        params.append('filters[page][type][]', 'news');
                        break;
                    case 'page':
                        params.append('filters[page][type][]', 'page');
                        break;
                }
            }

            // Makes use of the `internal_weight` during search (this means that results with higher weights will appear on earlier pages)
            // See (https://swiftype.com/documentation/site-search/searching/boosting)
            params.append('functional_boosts[page][internal_weight]', 'linear');

            return 'https://api.swiftype.com/api/v1/public/engines/search.json?' + params.toString();
        },
        makeUpcomingEventsSwiftypeUrl: function (query) {
            let params = {
                q: query,
                per_page: this.options.resultsPerPage,
                spelling: 'always',
                engine_key: this.searchEngineKey,
            }

            if (this.hasPage()) {
                params['page'] = this.getPage();
            }

            params = new URLSearchParams(params);

            params.append('highlight_fields[page][body][size]', 275 + 25); // we'll trim to 275, but give it a buffer
            params.append('highlight_fields[page][body][fallback]', true);

            params.append('filters[page][type][]', 'event');
            params.append('filters[page][internal_weight]', '1');   // Only

            // Makes use of the `internal_weight` during search (this means that results with higher weights will appear on earlier pages)
            // See (https://swiftype.com/documentation/site-search/searching/boosting)
            // params.append('functional_boosts[page][internal_weight]', 'linear');

            return 'https://api.swiftype.com/api/v1/public/engines/search.json?' + params.toString();
        },
        getUpcomingEventsResults: function (query) {
            const url = this.makeUpcomingEventsSwiftypeUrl(query);

            return fetchPolyfill(url).then(response => response.json());
        },
        getSuggestions: function (query) {
            const url = new URL('https://search-api.swiftype.com/api/v1/public/engines/search.json');
            url.searchParams.append('engine_key', this.searchEngineKey);
            url.searchParams.append('q', query);
            url.searchParams.append('spelling', 'always');

            return fetchPolyfill(url.toString())
                .then(res => res.json());
        },
        getResults: function (query) {
            const url = this.makeSwiftypeUrl(query);

            return fetchPolyfill(url).then(response => response.json());
        },
        processUpcomingEventsResults: function (results) {
            const upcomingBlocks = [];

            for (let result of results) {
                if (get(result, 'hidden, false')) {
                    continue;
                }

                let event = this.processEvent(result, results, upcomingBlocks);
                this.ViewModel.UpcomingTotal++;
                this.ViewModel.UpcomingSlider.push({
                    "Link": event.item.url,
                    "ListingImage": event.scroller_image,
                    "Title": event.item.internal_title,
                    "Prefix": event.item.prefix || "",
                    "Suffix": event.item.suffix || "",
                    "Date": event.display_start_date,
                });

                if (this.ViewModel.UpcomingSlider.length) {
                    const scroller = this.$el.querySelector('.search-scroller');
                    setTimeout(() => {
                        initialiseElement(scroller);
                        focus();
                    }, 500);
                }

            }
            this.ViewModel.results = upcomingBlocks;

        },
        processPageResults: function (results) {
            const blocks = [];

            for (let result of results) {
                if (get(result, 'hidden', false)) {
                    continue;
                }

                let relativeUrl = result.url.replace(/^https?:\/\/[^/]+\//, '');
                result.relative_url = relativeUrl;

                switch(result.type) {

                    case 'past_event':
                    case 'event':
                        blocks.push(
                            this.processEvent(result, results, blocks)
                        );
                        break;
                    case 'news':
                        blocks.push(
                            this.processNews(result, results, blocks)
                        );
                        break;
                    default:
                        blocks.push(
                            this.processDefault(result, results, blocks)
                        );
                        break;
                }
            }
            this.ViewModel.Results = blocks;
        },
        processEvent: function (result) {
            const cssClasses = {
                'search-result': true,
                'search-result--default': true,
            };
            const inFuture = result.end_date ? now < (DateTime.fromISO(result.end_date, {zone: TIMEZONE})) : now < (DateTime.fromISO(result.start_date, {zone: TIMEZONE}));

            if (result.image && inFuture) {
                cssClasses['search-result--image'] = true;
            }

            const display_start_date = DateTime.fromISO(result.start_date, {zone: TIMEZONE}).toFormat('ccc LLL d\'&nbsp;&nbsp;\'  ha \'CT\'');
            const display_end_date = DateTime.fromISO(result.end_date, {zone: TIMEZONE}).toFormat('ccc \'<span>\'LLL d\'</span>\'');
            const display_date_range_start = DateTime.fromISO(result.start_date, {zone: TIMEZONE}).toFormat('ccc LLL d');
            const display_date_range_end = result.end_date ? DateTime.fromISO(result.end_date, {zone: TIMEZONE}).toFormat('\' –\' ccc LLL d\',\' y') : DateTime.fromISO(result.start_date, {zone: TIMEZONE}).toFormat('\',\' y');

            return {
                type: inFuture ? 'event' : 'pastevent',
                display_type: inFuture ? 'Event' : 'Past Event',
                display_image: (result.image && inFuture) ?
                    image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 480, width: 565, quality: 'auto'}) :
                    false,
                scroller_image: image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 480, width: 565, quality: 'auto'}),
                item: result,
                cssClasses,
                display_start_date,
                display_date_range_start,
                display_date_range_end,
                display_end_date,
                summary: result.swiftype_summary ? result.swiftype_summary : (result.highlight ? result.highlight.body : result.body)
            };
        },
        processNews: function (result) {
            const cssClasses = {
                'search-result': true,
                'search-result--news': true,
            };
            if (result.image) {
                cssClasses['search-result--image'] = true;
            }
            return {
                type: 'news',
                display_type: 'Stories',
                display_image: (result.image) ?
                    image.process(result.image, {crop: 'fill', format: 'auto', gravity: 'auto', height: 480, width: 565, quality: 'auto'}) :
                    false,
                item: result,
                cssClasses,
                summary: result.swiftype_summary ? result.swiftype_summary : (result.highlight ? result.highlight.body : result.body)
            };
        },
        processDefault: function (result) {
            const cssClasses = {
                'search-result': true,
                'search-result--default': true,
            };
            return {
                type: 'default',
                display_type: false,
                item: result,
                cssClasses,
                summary: result.swiftype_summary ? result.swiftype_summary : (result.highlight ? result.highlight.body : result.body)
            };
        },
        buildPagination: function (data) {
            const {
                total_result_count, per_page, current_page, num_pages, query
            } = data.info.page;

            const start = (current_page - 1) * per_page;

            // index of last item on this page
            const end = Math.min((current_page * per_page) - 1, total_result_count);

            // link to previous page, if exists
            const prev = current_page > 1 ? this.makeUrlWithPage(query, current_page - 1) : null;

            // link to next page, if exists
            const next = current_page < num_pages ? this.makeUrlWithPage(query,  current_page + 1) : null;

            let pages = [];

            for (let i = 1; i <= num_pages; i++) {
                // skip showing links to pages that aren't either:
                //  * the first few pages
                //  * the last page
                //  * within a range of current page
                if (i > 1 && i < num_pages && Math.abs(i - current_page) > 3) {
                    continue;
                }

                // the index of the first result on the given page
                let offset = i * per_page;

                pages.push({
                    number: i,
                    min: offset,
                    max: Math.min(total_result_count, offset + per_page),
                    is_current: i == current_page,
                    link: this.makeUrlWithPage(query, i)
                });
            }
            let lastPage = 0;
            pages.forEach(page => {
                // `jumped` is true if there where skipped pages (use to add ellipse via CSS)
                page.jumped = (lastPage !== (page.number - 1));
                lastPage = page.number;
            });

            this.ViewModel.CurrentPage = current_page;
            this.ViewModel.TotalPages = num_pages;
            this.ViewModel.TotalResults = total_result_count;

            this.ViewModel.Pagination = { start, end, prev, next, pages };
        },
        updateInput: function () {
            if (!this.searchInputSelector) {
                return;
            }

            const inputElement = document.querySelector(this.searchInputSelector);

            if (!inputElement) {
                return;
            }

            inputElement.value = this.ViewModel.Query;
        },
        updateTabs: function () {
            const activeTab = this.getActiveTab();

            const tabs = [{
                slug: 'all',
                is_active: activeTab === 'all',
                name: 'All',
                link: this.makeUrlWithTab(this.ViewModel.Query, 'all'),
            }];

            tabs.push({
                slug: 'event',
                is_active: activeTab === 'event',
                name: 'Concerts',
                link: this.makeUrlWithTab(this.ViewModel.Query, 'event'),
            });

            tabs.push({
                slug: 'stories',
                is_active: activeTab === 'stories',
                name: 'Stories',
                link: this.makeUrlWithTab(this.ViewModel.Query, 'stories'),
            });

            this.ViewModel.Tabs = tabs;
        },
        showResults: function (data, upcomingEventsData) {

            this.buildPagination(data);

            this.processPageResults(data.records.page);

            if (upcomingEventsData) {
                this.processUpcomingEventsResults(upcomingEventsData.records.page);
                upcomingEventsData.info.page.num_pages > 1 ? this.ViewModel.MoreConcertsResultLink = this.makeUrlWithTab(this.ViewModel.Query, 'event') : this.ViewModel.MoreConcertsResultLink = "";
            }

            this.ViewModel.Loading = false;
        },
    },
    beforeMount(){
        this.init();
    },
    mounted() {
        if (this.$el) {
            this.ViewModel.inputField = this.$el.querySelector('form input[name="q"]');
            this.ViewModel.inputField.addEventListener('keyup', this.getSuggestionsListener());
        }
    }
});
